import Box from "@mui/material/Box";
import LightTooltip from "controls/global/light-tooltip";
import React, {
  ReactNode,
  useRef,
  useState,
  useEffect,
} from "react";
import {
  isEllipsisShown,
  getEllipsisAppendedSubstring
} from "utils/shared";
import {
  styled,
  Typography,
} from "@mui/material";

interface Props {
  title: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal;
  children?: ReactNode;
  className?: string | undefined;
  sticky?: boolean;
  alwaysShowOnHover?: boolean;
  checkDataLimit?: boolean;
}
interface TextProps {
  children: ReactNode;
}

const StyledLink = styled(Box)({
  cursor: 'pointer',
  textDecoration: 'underline',
});

const Text = React.forwardRef(
  ({ children }: TextProps, ref: React.LegacyRef<HTMLDivElement>) => {
    return (
      <div
        ref={ref}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {children}
      </div>
    );
  }
);
Text.displayName = "Text";

const TooltipCell = ({ title, children, className, alwaysShowOnHover = false, checkDataLimit = false, }: Props) => {
  const [openTooltip, setOpenTooltip] = useState<boolean>(false);
  const inputElementRef = useRef<HTMLInputElement | null>(null);
  const [isDataLimitExceed, setIsDataLimitExceed] = useState(false);
  const [tooltipTipText, setToolTipText] = useState(title);

  useEffect(() => {
    if (isDataLimitExceed && title) {
      const tooltipTipTextMessage = getEllipsisAppendedSubstring(title.toString(), 200);
      setToolTipText(tooltipTipTextMessage);
    }
  }, [isDataLimitExceed]);

  const handleOpenTooltip = (open: boolean) => {
    const show = open &&
      (alwaysShowOnHover || isEllipsisShown(inputElementRef.current));
    if (checkDataLimit && inputElementRef.current?.scrollWidth! > 800) {
      setIsDataLimitExceed(true);
    }
    setOpenTooltip(show);
  };

  const getTitleToRender = () => {
    if (isDataLimitExceed) {
      return <>
        <Typography>{tooltipTipText} {isDataLimitExceed ? <StyledLink onClick={() => {
          setOpenTooltip(false);
        }
        }>View Full Message</StyledLink> : ''}</Typography>
      </>
    }
    else
      return title || "";
  }

  if (!title) return null;
  return (
    <Box
      onMouseEnter={() => handleOpenTooltip(true)}
      onMouseLeave={() => handleOpenTooltip(false)}
      className={className}
    >
      <LightTooltip
        arrow
        title={getTitleToRender()}
        open={openTooltip}>
        <Text ref={inputElementRef}>{children ?? title}</Text>
      </LightTooltip>
    </Box>
  );
};

export default TooltipCell;
